import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { 
  CheckCircle2, Circle, Trash2, Plus, Link2, FileText, 
  ExternalLink, Unlink, Sparkles
} from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { useState } from "react";
import { Input } from "@/components/ui/input";
import { DocumentLinkDialog } from "./DocumentLinkDialog";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface TenderTask {
  id: string;
  tender_id: string;
  label: string;
  is_completed: boolean;
  linked_document_id: string | null;
  sort_order: number;
  created_at: string;
}

interface LinkedDocument {
  id: string;
  title: string;
  file_name: string;
}

interface TenderChecklistProps {
  tenderId: string;
  tasks: TenderTask[] | undefined;
  isLoading: boolean;
}

export function TenderChecklist({ tenderId, tasks, isLoading }: TenderChecklistProps) {
  const queryClient = useQueryClient();
  const [newTaskLabel, setNewTaskLabel] = useState("");
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [selectedTaskForLink, setSelectedTaskForLink] = useState<TenderTask | null>(null);

  // Fetch linked documents
  const linkedDocumentIds = tasks?.map(t => t.linked_document_id).filter(Boolean) as string[] || [];
  
  const { data: linkedDocuments } = useQuery({
    queryKey: ["linked-documents", linkedDocumentIds],
    queryFn: async () => {
      if (linkedDocumentIds.length === 0) return {};
      
      const { data, error } = await supabase
        .from("documents")
        .select("id, title, file_name")
        .in("id", linkedDocumentIds);

      if (error) throw error;
      
      const map: Record<string, LinkedDocument> = {};
      data.forEach(doc => {
        map[doc.id] = doc;
      });
      return map;
    },
    enabled: linkedDocumentIds.length > 0,
  });

  // Add task mutation
  const addTask = useMutation({
    mutationFn: async (label: string) => {
      const maxOrder = tasks?.reduce((max, t) => Math.max(max, t.sort_order), -1) ?? -1;
      
      const { error } = await supabase
        .from("tender_tasks")
        .insert({
          tender_id: tenderId,
          label,
          sort_order: maxOrder + 1,
        });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tender-tasks", tenderId] });
      setNewTaskLabel("");
      toast.success("Tâche ajoutée");
    },
    onError: () => {
      toast.error("Erreur lors de l'ajout de la tâche");
    },
  });

  // Toggle task mutation
  const toggleTask = useMutation({
    mutationFn: async ({ taskId, isCompleted }: { taskId: string; isCompleted: boolean }) => {
      const { error } = await supabase
        .from("tender_tasks")
        .update({ is_completed: isCompleted })
        .eq("id", taskId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tender-tasks", tenderId] });
    },
    onError: () => {
      toast.error("Erreur lors de la mise à jour");
    },
  });

  // Delete task mutation
  const deleteTask = useMutation({
    mutationFn: async (taskId: string) => {
      const { error } = await supabase
        .from("tender_tasks")
        .delete()
        .eq("id", taskId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tender-tasks", tenderId] });
      toast.success("Tâche supprimée");
    },
    onError: () => {
      toast.error("Erreur lors de la suppression");
    },
  });

  // Link document mutation
  const linkDocument = useMutation({
    mutationFn: async ({ taskId, documentId }: { taskId: string; documentId: string | null }) => {
      const { error } = await supabase
        .from("tender_tasks")
        .update({ 
          linked_document_id: documentId,
          is_completed: documentId ? true : false // Auto-complete when linking
        })
        .eq("id", taskId);

      if (error) throw error;
    },
    onSuccess: (_, { documentId }) => {
      queryClient.invalidateQueries({ queryKey: ["tender-tasks", tenderId] });
      queryClient.invalidateQueries({ queryKey: ["linked-documents"] });
      toast.success(documentId ? "Document lié" : "Document délié");
      setLinkDialogOpen(false);
      setSelectedTaskForLink(null);
    },
    onError: () => {
      toast.error("Erreur lors de la liaison");
    },
  });

  const handleAddTask = (e: React.FormEvent) => {
    e.preventDefault();
    if (newTaskLabel.trim()) {
      addTask.mutate(newTaskLabel.trim());
    }
  };

  const openLinkDialog = (task: TenderTask) => {
    setSelectedTaskForLink(task);
    setLinkDialogOpen(true);
  };

  const completedCount = tasks?.filter(t => t.is_completed).length ?? 0;
  const linkedCount = tasks?.filter(t => t.linked_document_id).length ?? 0;
  const totalCount = tasks?.length ?? 0;
  const progressPercent = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  if (isLoading) {
    return (
      <div className="space-y-2">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  return (
    <>
      {/* Progress header */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-3 text-sm text-muted-foreground">
          <span>{completedCount}/{totalCount} complétées</span>
          {linkedCount > 0 && (
            <Badge variant="secondary" className="text-xs gap-1">
              <Link2 className="w-3 h-3" />
              {linkedCount} liés
            </Badge>
          )}
        </div>
        <span className="text-sm font-medium">{progressPercent}%</span>
      </div>

      {/* Progress bar */}
      {totalCount > 0 && (
        <div className="w-full bg-muted rounded-full h-2 mb-4">
          <div 
            className="bg-primary h-2 rounded-full transition-all duration-300"
            style={{ width: `${progressPercent}%` }}
          />
        </div>
      )}

      {/* Task list */}
      {tasks && tasks.length > 0 ? (
        <div className="space-y-1">
          {tasks.map((task) => {
            const linkedDoc = task.linked_document_id ? linkedDocuments?.[task.linked_document_id] : null;
            
            return (
              <div
                key={task.id}
                className={cn(
                  "flex items-center gap-2 p-2 rounded-lg hover:bg-muted/50 group transition-colors",
                  task.is_completed && "opacity-70"
                )}
              >
                {/* Checkbox */}
                <button
                  onClick={() => toggleTask.mutate({ taskId: task.id, isCompleted: !task.is_completed })}
                  className="shrink-0"
                >
                  {task.is_completed ? (
                    <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                  ) : (
                    <Circle className="w-5 h-5 text-muted-foreground hover:text-primary transition-colors" />
                  )}
                </button>
                
                {/* Label and linked doc */}
                <div className="flex-1 min-w-0">
                  <span className={cn(
                    "text-sm block",
                    task.is_completed && "line-through text-muted-foreground"
                  )}>
                    {task.label}
                  </span>
                  
                  {linkedDoc && (
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <FileText className="w-3 h-3 text-emerald-600" />
                      <span className="text-xs text-emerald-600 truncate">
                        {linkedDoc.title}
                      </span>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            onClick={() => linkDocument.mutate({ taskId: task.id, documentId: null })}
                            className="text-muted-foreground hover:text-destructive transition-colors"
                          >
                            <Unlink className="w-3 h-3" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent>Délier le document</TooltipContent>
                      </Tooltip>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1 shrink-0">
                  {!linkedDoc && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-muted-foreground hover:text-primary"
                          onClick={() => openLinkDialog(task)}
                        >
                          <Link2 className="w-4 h-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Lier un document</TooltipContent>
                    </Tooltip>
                  )}
                  
                  <Button
                    variant="ghost"
                    size="icon"
                    className="opacity-0 group-hover:opacity-100 h-7 w-7 text-muted-foreground hover:text-destructive transition-all"
                    onClick={() => deleteTask.mutate(task.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground text-center py-4">
          Aucune tâche pour l'instant
        </p>
      )}

      {/* Add task form */}
      <form onSubmit={handleAddTask} className="flex items-center gap-2 pt-3 mt-2 border-t">
        <Input
          placeholder="Ajouter une pièce à fournir..."
          value={newTaskLabel}
          onChange={(e) => setNewTaskLabel(e.target.value)}
          className="h-9"
        />
        <Button 
          type="submit" 
          size="sm" 
          disabled={!newTaskLabel.trim() || addTask.isPending}
          className="shrink-0"
        >
          <Plus className="w-4 h-4" />
        </Button>
      </form>

      {/* Link dialog */}
      {selectedTaskForLink && (
        <DocumentLinkDialog
          open={linkDialogOpen}
          onOpenChange={setLinkDialogOpen}
          taskLabel={selectedTaskForLink.label}
          onSelect={(doc) => {
            linkDocument.mutate({ 
              taskId: selectedTaskForLink.id, 
              documentId: doc.id 
            });
          }}
        />
      )}
    </>
  );
}
